import {
  chakraComponents,
  DropdownIndicatorProps,
  OptionProps,
  Select as ChakraReactSelect,
} from 'chakra-react-select';

import { T, useTranslation } from '@oaknorth/util-intl';

import { Icon, LEGACY_MODE, Spacer, useMode } from '../chakra';
import { MdCheckCircleOutline, MdExpandMore } from '../icons';

import { SelectProps } from './types';
import { getChakraStylesWithDefaults, getOptionsAreGrouped, normaliseGroups } from './utils';

export const SELECT_INDICATOR_SIZE = 7;
const DropdownIndicator = <OptionType, IsMultiSelect extends boolean>(
  props: DropdownIndicatorProps<OptionType, IsMultiSelect>
) => {
  const { mode } = useMode();
  return (
    <chakraComponents.DropdownIndicator {...props}>
      <Icon
        as={MdExpandMore}
        boxSize={mode === LEGACY_MODE ? 3 : SELECT_INDICATOR_SIZE}
        color={mode === LEGACY_MODE ? 'fg' : 'gray.400'}
      >
        <MdExpandMore />
      </Icon>
    </chakraComponents.DropdownIndicator>
  );
};

const OptionWithCheck = <OptionType, IsMultiSelect extends boolean>({
  children,
  ...props
}: OptionProps<OptionType, IsMultiSelect>) => {
  return (
    <chakraComponents.Option {...props}>
      {children}
      <Spacer />
      {props.isSelected ? (
        <Icon color="blue.500" marginLeft={2}>
          <MdCheckCircleOutline />
        </Icon>
      ) : null}
    </chakraComponents.Option>
  );
};

/**
 * A select component that can be used as a single or multi select when options are objects.
 * If you need a select whose options are not objects, use either SingleItemSelect or MultiItemSelect
 * depending on whether you need a single or multi select. Both of those components are wrappers for this
 * select that makes non-object item select simpler to implement
 */
export const Select = <OptionType, IsMultiSelect extends boolean = false>({
  placeholder = T`select.defaultPlaceholder`,
  options: optionsProp,
  isSearchable = false,
  getOptionLabel: getOptionLabelProp,
  getOptionValue,
  inputId,
  chakraStyles,
  isMulti,
  components,
  ...props
}: SelectProps<OptionType, IsMultiSelect>) => {
  const { i18n } = useTranslation();

  const optionsAreGrouped = getOptionsAreGrouped<OptionType>(optionsProp);

  /**
   * SelectProps allows group headers to be InterpolateElements and allows
   * getOptionLabel to return InterpolateElements for convenience when building options,
   * but ReactSelect expects them to be strings, so we must convert them before passing to ChakraReactSelect
   */
  const options = optionsAreGrouped ? normaliseGroups(optionsProp, i18n) : optionsProp;

  const getOptionLabel = (option: OptionType) => i18n.stringify(getOptionLabelProp(option));

  return (
    <ChakraReactSelect<OptionType, IsMultiSelect>
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder}
      isSearchable={isSearchable}
      inputId={inputId}
      menuPlacement="auto"
      isMulti={isMulti}
      //eslint-disable-next-line @typescript-eslint/naming-convention -- DropdownIndicator is a component name
      components={{ DropdownIndicator, Option: isMulti ? OptionWithCheck : chakraComponents.Option, ...components }}
      chakraStyles={getChakraStylesWithDefaults(chakraStyles, isMulti || false)}
      hideSelectedOptions={false}
      {...props}
    />
  );
};
